class InfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClickHandler.bind(this));
  }

  connectedCallback() {
    if (this.dataset.trigger != 'infinite') return;
    // The reserved rows sit between the last card and this element, so fetch once the last card is within a screen of view,
    // or straight away if the shopper has already scrolled past this element
    const reserved = this.reserveSpace();
    const screen = window.innerHeight;
    new IntersectionObserver(this.handleIntersection.bind(this), {rootMargin: `${screen}px 0px ${reserved + screen}px 0px`}).observe(this);
  }

  // Leave room inside the grid for the coming rows (up to two pages), so new cards fill it in place. Otherwise a fast scroll
  // reaches the footer before the products arrive, and they then push it away (a large layout shift for Core Web Vitals)
  reserveSpace() {
    const grid = document.getElementById('product-grid');
    const cards = grid ? Array.from(grid.children) : [];
    const reserveCount = parseInt(this.dataset.reserveCount);
    if (!cards.length || !reserveCount) return 0;
    const columns = cards.filter(card => card.offsetTop === cards[0].offsetTop).length;
    const rowHeight = cards.length > columns ? cards[columns].offsetTop - cards[0].offsetTop : cards[0].offsetHeight;
    const reserved = Math.ceil(reserveCount / columns) * rowHeight;
    grid.style.paddingBottom = reserved + 'px';
    return reserved;
  }

  onClickHandler() {
    if (InfiniteScroll.restoring || this.classList.contains('loading') || this.classList.contains('disabled')) return;
    this.classList.add('loading');
    this.classList.add('disabled');

    const sections = InfiniteScroll.getSections();
    sections.forEach(() => {
      const url = this.dataset.url;
      InfiniteScroll.renderSectionFromFetch(url);
    });
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting || InfiniteScroll.restoring) return;
    observer.unobserve(this);

    this.onClickHandler();
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  // Ask Shopify for just the product grid section instead of the whole page
  static sectionUrl(url) {
    const sectionId = InfiniteScroll.getSections()[0].section;
    if (!sectionId) return url;
    const sectionUrl = new URL(url, window.location.origin);
    sectionUrl.searchParams.set('section_id', sectionId);
    return sectionUrl.pathname + sectionUrl.search;
  }

  static renderSectionFromFetch(url) {
    fetch(InfiniteScroll.sectionUrl(url))
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        // Cards first, into the space reserved for them; the page must never get shorter mid-update, or the
        // browser pulls the scroll position up and its scroll anchoring can then jump the shopper to the footer
        InfiniteScroll.renderProductGridContainer(html);
        InfiniteScroll.renderPagination(html);
        InfiniteScroll.trackLoadedUrl(url);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  static renderPagination(html) {
    const container = document.getElementById('ProductGridContainer').querySelector('.pagination-wrapper');
    const pagination = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').querySelector('.pagination-wrapper');
    if (pagination) {
      container.innerHTML = pagination.innerHTML;
    }
    else {
      container.remove();
    }
    // A new infinite-scroll element has already reserved room for the following page; without one, nothing is left to load
    if (!container.querySelector('infinite-scroll')) document.getElementById('product-grid').style.paddingBottom = '';
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('product-grid');
    const products = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
    container.insertAdjacentHTML('beforeend', products.innerHTML);
  }

  // Kept on the grid element itself, so it resets when filters re-render the grid
  static trackLoadedUrl(url) {
    const grid = document.getElementById('product-grid');
    grid.loadedUrls = (grid.loadedUrls || []).concat(url);
  }

  // Address of this listing, ignoring tracking parameters (in Safari, Shopify can bounce Back through shop.app and add ?_su_rec=…)
  static pageKey() {
    const params = new URLSearchParams(window.location.search);
    Array.from(params.keys()).filter(key => key.startsWith('_')).forEach(key => params.delete(key));
    return window.location.pathname + '?' + params.toString();
  }

  // On leaving the page, remember which extra pages were loaded and which card was at the top of the screen
  static saveState() {
    const grid = document.getElementById('product-grid');
    try {
      history.scrollRestoration = 'auto';
      const cards = grid ? Array.from(grid.children) : [];
      if (!cards.length) {
        sessionStorage.removeItem('infinite-scroll-state');
        return;
      }
      let index = cards.findIndex(card => card.getBoundingClientRect().bottom > 0);
      if (index === -1) index = cards.length - 1;
      sessionStorage.setItem('infinite-scroll-state', JSON.stringify({
        page: InfiniteScroll.pageKey(),
        urls: grid.loadedUrls || [],
        index: index,
        top: cards[index].getBoundingClientRect().top
      }));
    } catch (e) {}
  }

  // When Back reloads the page (instead of restoring it from the browser's cache), load those pages again and return to the same card.
  // Done here rather than left to the browser, which on slow loads can give up and leave the shopper at the top of the page.
  static restoreState() {
    let state;
    try {
      state = JSON.parse(sessionStorage.getItem('infinite-scroll-state'));
      sessionStorage.removeItem('infinite-scroll-state');
    } catch (e) {
      return;
    }
    const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    const grid = document.getElementById('product-grid');
    if (!state || !navigation || navigation.type !== 'back_forward' || !grid) return;
    if (state.page !== InfiniteScroll.pageKey()) return;

    // Stop the browser restoring its old scroll offset, which ignores the sticky header's offset and would land on top of ours
    history.scrollRestoration = 'manual';
    InfiniteScroll.restoring = true;
    Promise.all(state.urls.map(url => fetch(InfiniteScroll.sectionUrl(url)).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    })))
      .then((pages) => {
        if (pages.length) {
          pages.forEach(html => InfiniteScroll.renderProductGridContainer(html));
          InfiniteScroll.renderPagination(pages[pages.length - 1]);
          grid.loadedUrls = state.urls;
        }
        InfiniteScroll.restoring = false;
        const card = grid.children[state.index];
        if (!card) {
          history.scrollRestoration = 'auto';
          return;
        }

        // Keep the card where it was while the rest of the page loads (the sticky header and app badges can still shift it),
        // until the page has finished loading or the shopper starts scrolling
        let holding = true;
        const release = () => {
          holding = false;
          history.scrollRestoration = 'auto';
        };
        ['touchstart', 'wheel', 'keydown', 'mousedown'].forEach(type => window.addEventListener(type, release, {once: true, passive: true}));
        if (document.readyState === 'complete') setTimeout(release, 1000);
        else window.addEventListener('load', () => setTimeout(release, 1000), {once: true});
        const hold = () => {
          if (!holding) return;
          const offset = Math.round(card.getBoundingClientRect().top - state.top);
          if (offset) window.scrollBy(0, offset);
          requestAnimationFrame(hold);
        };
        hold();
      })
      .catch((e) => {
        console.error(e);
        InfiniteScroll.restoring = false;
        history.scrollRestoration = 'auto';
      });
  }
}
customElements.define('infinite-scroll', InfiniteScroll);

InfiniteScroll.restoreState();
window.addEventListener('pagehide', InfiniteScroll.saveState);
